import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Popconfirm, Tag, Tooltip, message,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { captureAnalytics } from '../../actions/preload';
import {
  AWAITING_PROVIDER_LABEL, canHoldDocument, describeHold, removeAwaitingProviderHold,
} from './awaitingProvider';
import './AwaitingProviderChip.scss';

// ENG-3659: shown under the patient on an Inbox or Referral Management row the
// user set aside as "Awaiting Provider". The row payload only carries
// `awaiting_provider` while the hold applies, and only for organizations that
// have it. The × removes the hold; onRemoved lets the page refresh its rows.
const AwaitingProviderChip = ({ record, onRemoved }) => {
  // The hold the user removed, hidden until the refresh drops it. A hold placed
  // again later has a new marked_at, so it shows on this same row.
  const [removedHold, setRemovedHold] = useState(null);
  const [removing, setRemoving] = useState(false);
  const hold = record?.awaiting_provider;
  const holdKey = hold ? `${record.parent_type}:${record.send_token}:${hold.marked_at}` : null;
  if (!hold || holdKey === removedHold) return null;

  const target = { parentType: record.parent_type, sendToken: record.send_token };
  const removable = canHoldDocument(target.parentType, target.sendToken);

  const remove = async () => {
    setRemoving(true);
    try {
      await removeAwaitingProviderHold(target);
      setRemovedHold(holdKey);
      captureAnalytics('awaiting_provider_removed', { source: 'row_chip' });
      onRemoved();
    } catch {
      message.error('Could not remove Awaiting Provider. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {/* An en space, not a margin, after the DOB: the line can break after it,
          so when the chip wraps in a narrow column it stays left-aligned. */}
      {'\u2002'}
      {/* The whole row opens the document; clicks on the chip must not. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span className="awaiting-provider-chip" onClick={(event) => event.stopPropagation()}>
        <Tooltip title={`${describeHold(hold)} It drops off once the document is filed.`}>
          <Tag color="gold" className="awaiting-provider-chip__tag">
            {AWAITING_PROVIDER_LABEL}
          </Tag>
        </Tooltip>
        {removable && (
          <Popconfirm
            title="Remove Awaiting Provider from this document?"
            okText="Remove"
            cancelText="Keep"
            // Not returning the promise: the chip unmounts on success, and antd's
            // confirm button would otherwise update itself after that.
            onConfirm={() => { remove(); }}
          >
            <button
              type="button"
              className="awaiting-provider-chip__remove"
              aria-label="Remove Awaiting Provider"
              disabled={removing}
            >
              <CloseOutlined />
            </button>
          </Popconfirm>
        )}
      </span>
    </>
  );
};

AwaitingProviderChip.propTypes = {
  record: PropTypes.shape({
    awaiting_provider: PropTypes.shape({ marked_at: PropTypes.string, marked_by: PropTypes.string }),
    parent_type: PropTypes.string,
    send_token: PropTypes.string,
  }),
  onRemoved: PropTypes.func,
};

AwaitingProviderChip.defaultProps = {
  record: null,
  onRemoved: () => {},
};

export default AwaitingProviderChip;
